/**
 * #557 Share Map Link — capture helpers.
 *
 * `buildPresetData` is the single source of truth for serializing the live
 * layer state into the preset shape. It is the exact logic SavePresetModal
 * used inline; extracted here so both "Save Preset" and "Share map link"
 * capture identically and stay in sync.
 *
 * Viewport (center/zoom) is captured separately by the caller because presets
 * do not store it.
 */

import { Layer } from '../../types/layer';
import { LiveLayerDef } from '../layerDrawer/LayerDrawerContext';
import { LayerRegistry } from '../layers2';
import { PresetData, PresetLayerState, PresetLiveLayerState } from '../../types/layerPreset';

/**
 * Serialize the current enabled layer/live-layer/basemap state into PresetData.
 * Reads live opacity/order from the registry instance when present, falling
 * back to the layer's stored defaults.
 */
/**
 * Ephemeral layers (#802) exist only for the current session — a drawn AFF
 * aircraft track is registered as one so it gets the standard drawer controls.
 * Their ids embed a timestamp and can never resolve again, so they are excluded
 * from every capture: saved presets and share links alike.
 */
function isEphemeral(layer: Layer): boolean {
  return layer.metadata?.ephemeral === true;
}

export function buildPresetData(
  layers: Layer[],
  liveLayers: LiveLayerDef[],
  registry: LayerRegistry | null,
  map?: { getCenter: () => { lng: number; lat: number }; getZoom: () => number } | null,
): PresetData {
  const capturable = layers.filter((layer) => !isEphemeral(layer));

  const layerStates: PresetLayerState[] = capturable
    .filter((layer) => layer.enabled)
    .map((layer) => {
      const instance = registry?.get(layer.id);
      return {
        id: layer.id,
        enabled: true,
        opacity: instance?.getOpacity() ?? layer.default_opacity,
        order: instance?.getOrder() ?? layer.default_order,
      };
    });

  const liveLayerStates: PresetLiveLayerState[] = liveLayers
    .filter((layer) => layer.enabled)
    .map((layer) => ({ id: layer.id, enabled: true }));

  const backgroundLayer = capturable.find(
    (layer) => layer.category === 'backgrounds' && layer.enabled,
  );

  const data: PresetData = {
    layers: layerStates,
    liveLayers: liveLayerStates,
    backgroundLayerId: backgroundLayer?.id || null,
  };

  // #557 — capture viewport so a saved map restores center/zoom too.
  if (map) {
    const center = map.getCenter();
    data.viewport = { lng: center.lng, lat: center.lat, zoom: map.getZoom() };
  }

  return data;
}

/** Build the permalink URL for a saved map id. */
export function buildPermalinkUrl(origin: string, id: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}/map?permalink=${id}`;
}
